import { FC, useLayoutEffect, useRef } from 'react';
import { Paper } from '@material-ui/core';

import { TabActiveProvider, TabItem, useTabs } from 'contexts/TabContext';
import { TabRecordType, TabSection } from 'constants/tabs';
import { RECORD_PAGE, SECTION_PAGE } from './pageRegistry';

/** The shared scroll container, owned by App.tsx. */
export const MAIN_SCROLL_ID = 'app-main';

const renderTabContent = (tab: TabItem) => {
  if (tab.pinned) {
    const ListPage = SECTION_PAGE[tab.recordId as TabSection];
    return ListPage ? <ListPage /> : null;
  }

  const DetailPage = RECORD_PAGE[tab.type as TabRecordType];
  return DetailPage && tab.recordId ? <DetailPage recordId={tab.recordId} innerTab={tab.innerTab} /> : null;
};

const paneKey = (tab: TabItem, pinnedVersion: number) => {
  const key = `${tab.key}:${tab.version || 0}`;
  return tab.pinned ? `${key}:${pinnedVersion}` : key;
};

/**
 * Renders every open tab at once and shows one of them.
 *
 * Inactive tabs are hidden, never unmounted — that is what makes switching tabs free of API calls
 * and keeps each tab's form state and scroll position intact (TAB-03). The cost is real, and it is
 * why the tab count is capped.
 */
const TabHost: FC = () => {
  const { isTabNavActive, tabs, activeKey, pinnedVersion } = useTabs();

  const scrollPositions = useRef<Record<string, number>>({});
  const previousKey = useRef<string>(activeKey);

  // Every tab shares one scroll container, so its position has to be handed over by hand.
  // Layout effect, not effect: the restore has to happen before the browser paints, or it flickers.
  useLayoutEffect(() => {
    if (previousKey.current === activeKey) {
      return;
    }

    const main = document.getElementById(MAIN_SCROLL_ID);
    if (main) {
      scrollPositions.current[previousKey.current] = main.scrollTop;
      main.scrollTop = scrollPositions.current[activeKey] || 0;
    }

    previousKey.current = activeKey;
  }, [activeKey]);

  const panes = tabs.map(tab => ({ tab, content: renderTabContent(tab) }));

  // A section TabHost does not render would otherwise leave an empty white panel above the page
  // the route is still drawing.
  if (!isTabNavActive || !panes.some(pane => pane.content)) {
    return null;
  }

  /**
   * The same white surface the routed pages sit on.
   *
   * Routes render inside a Paper in App.tsx; tab content renders outside it, so without this the
   * page would sit straight on the grey app background and look nothing like the same screen with
   * the tab bar switched off.
   */
  return (
    <Paper elevation={0} style={{ marginBottom: 16, marginRight: 16 }}>
      {panes.map(({ tab, content }) => (
        // The key carries versions so refreshPinnedTab and refreshTab can remount a pane — the only way
        // to make a page that has been mounted all along go and fetch again.
        <div key={paneKey(tab, pinnedVersion)} style={{ display: tab.key === activeKey ? 'block' : 'none' }}>
          <TabActiveProvider value={tab.key === activeKey}>{content}</TabActiveProvider>
        </div>
      ))}
    </Paper>
  );
};

export default TabHost;
